import { Theme, css } from '@emotion/react'
import styled from '@emotion/styled'
import { PageProps, graphql } from 'gatsby'
import { useIntl } from 'gatsby-plugin-react-intl'
import * as React from 'react'
import ReactMarkdown from 'react-markdown'

import Container from '../components/Container'
import Layout from '../components/Layout'
import SEO from '../components/SEO/SEO'
import { ButtonStyled } from '../components/common/Button/styles'
import { useIntlLabel } from '../intl'
import { AboutPage } from './__generated__/AboutPage'
import HomeRequestSection from './home/HomeRequestSection'

const backgroundTopStyle = (image: string) => (theme: Theme) => {
    return css({
        display: 'flex',
        flexDirection: 'row',
        marginTop: '88px',
        background: `url(${image}) no-repeat top 24% center / 100%`,
        '@media (max-width: 1024px)': {
            justifyContent: 'center',
            marginTop: '80px',
            background: `url(${image}) no-repeat top 24% center / 100%`,
        },
        '@media (max-width: 768px)': {
            marginTop: '56px',
            background: `url(${image}) no-repeat top 28% center /  100%`,
        },
    })
}

const ImageStyled = styled.img({
    margin: 0,
    marginLeft: 275,
    '@media (max-width: 1024px)': {
        marginLeft: 0,
        justifyContent: 'center',
    },
    '@media (max-width: 768px)': {
        marginLeft: 0,
    },
})

const MessageContainerStyled = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: '48px',
    '@media (max-width: 768px)': {
        marginTop: '24px',
        paddingBottom: '24px',
        alignItems: 'normal',
        flexDirection: 'column-reverse',
    },
})

const bubbleHintStyle = (image: string) => (theme: Theme) => {
    return css({
        minWidth: 378,
        height: 308,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        background: `url(${image}) no-repeat`,
        '@media (max-width: 768px)': {
            display: 'none',
        },
    })
}

const MessageHintStyled = styled.p({
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '31px',
    lineHeight: '30px',
    color: '#FFFFFF',
    maxWidth: 240,
    marginTop: '156px',
    textAlign: 'center',
})

const TextTypoStyled = styled.div({
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '23px',
    marginTop: '24px',
    marginBottom: 48,
    '@media (max-width: 768px)': {
        marginBottom: 32,
    },
})

const FeatureContainer = styled.div({
    backgroundColor: '#153B50',
    paddingTop: '82px',
    paddingBottom: '82px',
    '@media (max-width: 1024px)': {
        paddingTop: '80px',
    },
    '@media (max-width: 768px)': {
        flexDirection: 'column',
        paddingTop: '56px',
        paddingBottom: '56px',
    },
})

const FeatureWrapperStyled = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
})

const FeatureItem = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: '32px',
    flexBasis: '33.33%',
    '&:last-child': {
        marginRight: 0,
    },
    '@media (max-width: 1024px)': {
        marginTop: '24px',
    },
    '@media (max-width: 768px)': {
        flexBasis: 'auto',
        marginTop: 0,
        marginRight: 0,
        marginBottom: '24px',
        '&:last-child': {
            marginBottom: 0,
        },
    },
})

const FeatureMessage = styled.div({
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '21px',
    maxWidth: '248px',
    marginLeft: '16px',
    color: '#FFFFFF',
    '@media (max-width: 768px)': {
        maxWidth: 'none',
    },
    '& p': {
        marginBottom: 0,
    },
    '& p b': {
        fontSize: '16px',
        lineHeight: '19px',
    },
    '& div': {
        marginTop: 8,
        opacity: 0.6,
        fontSize: '14px',
        lineHeight: '18px',
    },
})

const FeatureImage = styled.img({
    width: 52,
    height: 52,
})

// SURVEY
const surveyContainerStyle = (image: string) => (theme: Theme) => {
    return css({
        background: `url(${image}) no-repeat bottom right`,
        paddingBottom: '82px',
        backgroundColor: '#43BC9C',
        '@media (max-width: 1024px)': {
            background: `url(${image}) no-repeat bottom right / 1016px 103px`,
            backgroundColor: '#43BC9C',
        },
        '@media (max-width: 768px)': {
            paddingBottom: '56px',
            background: `url(${image}) no-repeat bottom right / 917px 93px`,
            backgroundColor: '#43BC9C',
        },
    })
}

const bubbleHintReverseStyle = (image: string) => (theme: Theme) => {
    return css({
        minWidth: 378,
        height: 308,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        background: `url(${image}) no-repeat top -104px center`,
        padding: 0,
        margin: 0,
        border: 'none',
        '@media (max-width: 768px)': {
            minWidth: 189,
            height: 152,
            justifyContent: 'flex-end',
            background: `url(${image}) no-repeat top 110% right / 189px 189px`,
        },
    })
}

const SurveyMessageHintStyled = styled.p({
    fontFamily: ['DM Sans', 'sans-serif'].join(','),
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '31px',
    lineHeight: '30px',
    color: '#FFFFFF',
    maxWidth: 300,
    textAlign: 'center',
    marginTop: '56px',
    marginBottom: 0,
    '@media (max-width: 768px)': {
        marginTop: '24px',
        marginRight: '40px',
        fontSize: '16px',
        lineHeight: '23px',
        maxWidth: 106,
    },
})

const SurveyMessageContainerStyled = styled.div({
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 88,
    paddingRight: 30,
    alignItems: 'start',
    '@media (max-width: 1024px)': {
        paddingTop: '77px',
    },
    '@media (max-width: 768px)': {
        paddingTop: 0,
        paddingRight: 0,
        alignItems: 'normal',
    },
})

const ActionWrapperStyled = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
        marginBottom: '72px',
        flexDirection: 'column-reverse',
    },
})

const ActionButtonStyled = styled.a({
    cursor: 'pointer',
    color: '#FFFFFF',
    background: '#153B50',
    fontFamily: ['DM Sans', 'sans-serif'].join(','),
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '16px',
    lineHeight: '21px',
    padding: '16px 32px',
    border: 'none',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    borderRadius: 4,
    textAlign: 'center',
})

interface AboutPageProps extends PageProps {
    data: AboutPage
}

const About: React.FC<AboutPageProps> = ({ data }) => {
    const intl = useIntl()
    const getIntlLabel = useIntlLabel()

    return (
        <Layout>
            <SEO
                title={intl.formatMessage({ id: 'site_about_title' })}
                description={getIntlLabel(data.strapiAbout?.main, 'description')}
                pageUrl="/apropos"
            />
            <Container>
                <div
                    css={backgroundTopStyle(
                        data.strapiAbout?.watermark?.localFile?.publicURL ?? '',
                    )}
                >
                    <ImageStyled
                        src={data.strapiAbout?.landingPageCoverImage?.localFile?.publicURL ?? ''}
                        alt="COVER IMAGE TOROLALANA"
                    />
                </div>
                <MessageContainerStyled>
                    <div
                        css={bubbleHintStyle(
                            data.strapiAbout?.main?.dialogBackground?.localFile?.publicURL ?? '',
                        )}
                    >
                        <MessageHintStyled>
                            {getIntlLabel(data.strapiAbout?.main, 'dialogText')}
                        </MessageHintStyled>
                    </div>
                    <TextTypoStyled>
                        <ReactMarkdown>
                            {getIntlLabel(data.strapiAbout?.main, 'description')}
                        </ReactMarkdown>
                    </TextTypoStyled>
                </MessageContainerStyled>
            </Container>
            <FeatureContainer>
                <Container>
                    <FeatureWrapperStyled>
                        {data.strapiAbout?.features?.map((item, index) => (
                            <FeatureItem key={index}>
                                <FeatureImage
                                    src={item?.icon?.localFile?.publicURL ?? ''}
                                    alt={`TIPS IMAGE ${index}`}
                                />
                                <FeatureMessage>
                                    <p>
                                        <b>{getIntlLabel(item, 'title')}</b>
                                        <div>{getIntlLabel(item, 'description')}</div>
                                    </p>
                                </FeatureMessage>
                            </FeatureItem>
                        ))}
                    </FeatureWrapperStyled>
                </Container>
            </FeatureContainer>
            <div
                css={surveyContainerStyle(
                    data.strapiAbout?.survey?.watermark?.localFile?.publicURL ?? '',
                )}
            >
                <Container>
                    <ActionWrapperStyled>
                        <SurveyMessageContainerStyled>
                            <TextTypoStyled>
                                <ReactMarkdown>
                                    {getIntlLabel(data.strapiAbout?.survey, 'description')}
                                </ReactMarkdown>
                            </TextTypoStyled>
                            <ButtonStyled
                                color="secondary"
                                href={getIntlLabel(data.strapiAbout?.survey, 'gFormLink')}
                                target="_blank"
                            >
                                {getIntlLabel(data.strapiAbout, 'participateButton')}
                            </ButtonStyled>
                        </SurveyMessageContainerStyled>
                        <div
                            css={bubbleHintReverseStyle(
                                data.strapiAbout?.survey?.dialogBackground?.localFile?.publicURL ??
                                    '',
                            )}
                        >
                            <SurveyMessageHintStyled>
                                {getIntlLabel(data.strapiAbout?.survey, 'dialogText')}
                            </SurveyMessageHintStyled>
                        </div>
                    </ActionWrapperStyled>
                </Container>
            </div>
            <HomeRequestSection />
        </Layout>
    )
}

export default About

export const query = graphql`
    query AboutPage {
        strapiAbout {
            participateButton
            participateButton_mg
            gFormLink
            gFormLink_mg
            features {
                description
                description_mg
                title
                title_mg
                icon {
                    localFile {
                        publicURL
                    }
                }
            }
            landingPageCoverImage {
                localFile {
                    publicURL
                }
            }
            watermark {
                localFile {
                    publicURL
                }
            }
            main {
                description
                description_mg
                dialogText
                dialogText_mg
                dialogBackground {
                    localFile {
                        publicURL
                    }
                }
            }
            survey {
                description
                description_mg
                dialogText
                dialogText_mg
                watermark {
                    localFile {
                        publicURL
                    }
                }
                dialogBackground {
                    localFile {
                        publicURL
                    }
                }
            }
        }
    }
`
